import React from 'react';
import styled from 'styled-components';

import { PluginIconButton } from '../../topbar-plugins/shared/PluginControls';
import { NavigationShellPlugin } from '../../../packages/navigation-shell';
import { OnboardingNavigationCapabilities } from '../infrastructure/useCurrentAccount';

export interface OnboardingTopBarPluginOptions {
    accountCount?: number;
    appUrl?: string;
    businessName: string;
    capabilities: OnboardingNavigationCapabilities;
    email: string;
    mainAppUrl: string;
    pendoEnabled?: boolean;
    profilePhotoUrl?: string | null;
    currentUserId?: string;
    staffRole?: string | null;
    smsFeatureEnabled?: boolean | null;
    voipEnabled?: boolean | null;
    onNavigate?: (
        event: React.MouseEvent<HTMLAnchorElement>,
        url: string,
    ) => void;
}

const VoiceUnavailableButton = styled(PluginIconButton)`
    @media (max-width: 1199px) {
        display: none;
    }
`;

const voiceUnavailableFallback = (
    <VoiceUnavailableButton
        type="button"
        aria-label="Voice unavailable"
        aria-disabled="true"
        title="Voice is unavailable"
    >
        <i className="fal fa-phone-slash" aria-hidden="true" />
    </VoiceUnavailableButton>
);

const VoiceUnavailablePlugin = () => voiceUnavailableFallback;

export const createOnboardingTopBarPlugins = ({
    accountCount,
    appUrl,
    businessName,
    capabilities,
    email,
    mainAppUrl,
    onNavigate,
    pendoEnabled = false,
    profilePhotoUrl,
    currentUserId,
    staffRole,
    smsFeatureEnabled,
    voipEnabled,
}: OnboardingTopBarPluginOptions): NavigationShellPlugin[] => {
    const visibleQuickActionIds = [
        capabilities.canCreatePeople && 'navbar-add-client',
        capabilities.canBookAppointment && 'navbar-book-appointment',
        capabilities.canEnrollSession && 'navbar-enroll-in-session',
        capabilities.canCreateSubscription && 'navbar-create-subscription',
    ].filter((id): id is string => Boolean(id));

    return [
        ...(visibleQuickActionIds.length > 0
            ? [
                  {
                      id: 'quick-navigation',
                      label: 'Quick navigation',
                      load: async () => {
                          const { QuickNavigationPlugin } =
                              await import('../../topbar-plugins/quick-navigation/QuickNavigationPlugin');
                          return {
                              default: () => (
                                  <QuickNavigationPlugin
                                      mainAppUrl={mainAppUrl}
                                      onNavigate={onNavigate}
                                      visibleActionIds={visibleQuickActionIds}
                                  />
                              ),
                          };
                      },
                  },
              ]
            : []),
        ...(capabilities.canReadPeople
            ? [
                  {
                      id: 'search',
                      label: 'Search',
                      load: async () => {
                          const { SearchPlugin } =
                              await import('../../topbar-plugins/search/SearchPlugin');
                          return {
                              default: () => (
                                  <SearchPlugin
                                      businessName={businessName}
                                      mainAppUrl={mainAppUrl}
                                      onNavigate={onNavigate}
                                  />
                              ),
                          };
                      },
                  },
              ]
            : []),
        ...(capabilities.canViewReleaseNotes && pendoEnabled
            ? [
                  {
                      id: 'release-notes',
                      label: 'Release notes',
                      load: async () => {
                          const { ReleaseNotesPlugin } =
                              await import('../../topbar-plugins/release-notes/ReleaseNotesPlugin');
                          return {
                              default: ReleaseNotesPlugin,
                          };
                      },
                  },
              ]
            : []),
        ...(voipEnabled && capabilities.canReceiveVoice
            ? [
                  {
                      id: 'voice',
                      label: 'Voice',
                      component: VoiceUnavailablePlugin,
                  },
              ]
            : []),
        ...(smsFeatureEnabled && capabilities.canListMessages
            ? [
                  {
                      id: 'messages',
                      label: 'Messages',
                      load: async () => {
                          const { MessagesPlugin } =
                              await import('../../topbar-plugins/messages/MessagesPlugin');
                          return {
                              default: () => (
                                  <MessagesPlugin
                                      businessName={businessName}
                                      mainAppUrl={mainAppUrl}
                                      onNavigate={onNavigate}
                                  />
                              ),
                          };
                      },
                  },
              ]
            : []),
        {
            id: 'profile',
            label: 'Profile',
            load: async () => {
                const { OnboardingProfilePlugin } =
                    await import('../../topbar-plugins/profile/OnboardingProfilePlugin');
                return {
                    default: () => (
                        <OnboardingProfilePlugin
                            accountCount={accountCount}
                            appUrl={appUrl}
                            businessName={businessName}
                            canSwitchBusiness={capabilities.canSwitchBusiness}
                            canUseLiveSupport={capabilities.canUseLiveSupport}
                            canViewConnectionStatus={
                                Boolean(smsFeatureEnabled) &&
                                capabilities.canListMessages
                            }
                            currentUserId={currentUserId}
                            email={email}
                            mainAppUrl={mainAppUrl}
                            onMainAppClick={onNavigate}
                            profilePhotoUrl={profilePhotoUrl}
                            staffRole={staffRole}
                        />
                    ),
                };
            },
        },
    ];
};
